<template>
  <div class="row">
    <div class="col-xxl-12">
      <div class="card mb-25 border-0 rounded-0 bg-white pricing-box letter-spacing radius-7">
        <div class="card-body">
          <div class="card-head box-shadow bg-white d-lg-flex align-items-center justify-content-between p-15 p-sm-20 p-md-25 radius-7 mb-4">
            <div class="d-sm-flex align-items-center">
              <h4 class="mb-0">Preços por qualidade e período de armazenamento</h4>
            </div>

            <div class="d-flex align-items-center">
              <label class="me-2 mb-0">Período de armazenamento:</label>
              <select v-model="selectedStoragePeriod" class="form-select storage-select">
                <option value="all">Todos os períodos</option>
                <option value="0">Sem gravação</option>
                <option value="1">1 dia</option>
                <option value="3">3 dias</option>
                <option value="5">5 dias</option>
                <option value="7">7 dias</option>
                <option value="10">10 dias</option>
                <option value="20">20 dias</option>
                <option value="30">30 dias</option>
                <option value="60">60 dias</option>
                <option value="90">90 dias</option>
                <option value="120">120 dias</option>
                <option value="180">180 dias</option>
              </select>
            </div>
          </div>

          <div class="row">
            <div class="col-12 mb-4">
              <div class="alert alert-info">
                Os valores da sua fatura são gerados automaticamente com base nos serviços utilizados.
              </div>
            </div>

            <div class="area-prices col-12">
              <transition-group name="fade" tag="div" v-if="visiblePriceCategories.length > 0">
                <div
                  v-for="category in visiblePriceCategories"
                  :key="`${category.key}-section`"
                  class="mb-5"
                >
                  <h5 class="mb-3 d-flex align-items-center category-title">
                    <span class="badge me-2" :class="category.badgeClass">{{ category.badge }}</span>
                    <span>{{ category.title }}</span>
                  </h5>

                  <div class="row">
                    <div
                      v-for="(price, days) in category.prices"
                      :key="`${category.key}-${days}`"
                      class="col-lg-2 col-md-3 col-sm-6 mb-3"
                    >
                      <div class="card price-card h-100">
                        <div class="card-body">
                          <h6 class="card-title text-center mb-1">
                            <span v-if="days === '0'">Sem Gravação</span>
                            <span v-else-if="days === '1'">{{ days }} dia</span>
                            <span v-else>{{ days }} dias</span>
                          </h6>
                          <p class="text-center text-muted mb-3 storage-subtitle">
                            <small>período de armazenamento</small>
                          </p>
                          <div class="price-value text-center mb-3">
                            <span class="currency">R$</span>
                            <span class="amount">{{ formatPrice(price) }}</span>
                            <span class="period">/mês</span>
                          </div>
                          <p class="text-center text-muted mb-0">
                            <small>por câmera</small>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </transition-group>

              <div v-else-if="getLoadingPrices" class="text-center p-5">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Carregando...</span>
                </div>
                <p class="mt-3">Carregando tabela de preços...</p>
              </div>

              <div v-else class="text-center p-5 text-muted">
                Nenhum preço disponível para o filtro selecionado.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

const CATEGORY_META = {
  SD: {
    badge: 'SD',
    badgeClass: 'bg-secondary',
    title: 'Câmeras Standard Definition'
  },
  HD: {
    badge: 'HD',
    badgeClass: 'bg-primary',
    title: 'Câmeras High Definition'
  },
  FULLHD: {
    badge: 'FULL HD',
    badgeClass: 'bg-success',
    title: 'Câmeras Full High Definition'
  },
  '2K': {
    badge: '2K',
    badgeClass: 'bg-warning text-dark',
    title: 'Câmeras 2K Resolution'
  },
  '4K': {
    badge: '4K',
    badgeClass: 'bg-danger',
    title: 'Câmeras Ultra HD/4K'
  }
};

export default {
  name: "PricesContent",
  data() {
    return {
      selectedStoragePeriod: 'all'
    };
  },
  mounted(){
    this.setPrices();
  },
  computed:{
    ...mapGetters([
      "getPrices",
      "getLoadingPrices"
    ]),
    visiblePriceCategories() {
      if (!this.getPrices) {
        return [];
      }

      return Object.keys(this.getPrices)
        .map(categoryKey => {
          const prices = this.filteredPrices(categoryKey);

          if (Object.keys(prices).length === 0) {
            return null;
          }

          const meta = CATEGORY_META[categoryKey] || {
            badge: categoryKey,
            badgeClass: 'bg-secondary',
            title: `Câmeras ${categoryKey}`
          };

          return {
            key: categoryKey,
            prices,
            ...meta
          };
        })
        .filter(Boolean);
    }
  },
  methods: {
    ...mapActions([
      "setPrices"
    ]),
    formatPrice(price) {
      return Number(price).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    filteredPrices(category) {
      if (!this.getPrices || !this.getPrices[category]) {
        return {};
      }

      const prices = this.getPrices[category];

      if (this.selectedStoragePeriod === 'all') {
        return Object.keys(prices)
          .sort((a, b) => parseInt(a) - parseInt(b))
          .reduce((obj, key) => {
            obj[key] = prices[key];
            return obj;
          }, {});
      }

      return Object.keys(prices)
        .filter(day => day === this.selectedStoragePeriod)
        .reduce((obj, key) => {
          obj[key] = prices[key];
          return obj;
        }, {});
    }
  }
};
</script>

<style scoped>
.pricing-box {
  min-height: 500px;
}

.price-card {
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  margin-bottom: 10px;
  height: 100%;
}

.price-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
}

.price-card .card-body {
  padding: 16px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.category-title {
  font-weight: 600;
  padding-bottom: 8px;
  border-bottom: 1px solid #eee;
}

.price-value {
  font-size: 1.1rem;
}

.price-value .amount {
  font-size: 1.8rem;
  font-weight: 700;
  color: #333;
}

.price-value .currency,
.price-value .period {
  color: #666;
  font-weight: 500;
}

.storage-select {
  width: auto;
  min-width: 180px;
}

.storage-subtitle {
  font-size: 0.75rem;
  color: #999;
  line-height: 1;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

@media (max-width: 767.98px) {
  .price-card .card-body {
    padding: 12px;
  }

  .price-value .amount {
    font-size: 1.5rem !important;
  }

  .storage-subtitle {
    font-size: 0.7rem;
    margin-bottom: 8px;
  }
}
</style>
